import type { DatagridI18n } from '@juanwag/svelte-datagrid';
import { t } from '$lib/i18n';
import { formatWithoutExponent } from '@adminsoft/ui/utils/number-format';

export function getDatagridI18n(): DatagridI18n {
	return {
		// Search
		searchPlaceholder: t('datagridSearchPlaceholder'),
		searchPlaceholderEnhanced: t('datagridSearchPlaceholderEnhanced'),
		clearSearch: t('datagridClearSearch'),
		filters: t('datagridFilters'),

		// Row selection
		rowsSelected: (count) => t('datagridRowsSelected', { count: String(count) }),
		clearSelection: t('datagridClearSelection'),
		selectionActions: t('datagridSelectionActions'),
		copyRows: t('datagridCopyRows'),
		pinRows: t('datagridPinRows'),
		unpinRows: t('datagridUnpinRows'),
		expandRows: t('datagridExpandRows'),
		collapseRows: t('datagridCollapseRows'),
		toggleRowSelection: t('datagridToggleRowSelection'),
		rowSelectionLabel: t('datagridRowSelectionLabel'),
		selectEveryRow: t('datagridSelectEveryRow'),
		deselectEveryRow: t('datagridDeselectEveryRow'),
		selectCurrentPage: t('datagridSelectCurrentPage'),
		deselectCurrentPage: t('datagridDeselectCurrentPage'),
		selectRow: t('datagridSelectRow'),

		// Row expansion
		toggleRowExpansion: t('datagridToggleRowExpansion'),
		rowExpansionLabel: t('datagridRowExpansionLabel'),
		expandEveryRow: t('datagridExpandEveryRow'),
		collapseEveryRow: t('datagridCollapseEveryRow'),
		expandCurrentPage: t('datagridExpandCurrentPage'),
		collapseCurrentPage: t('datagridCollapseCurrentPage'),
		expandRowAria: t('datagridExpandRowAria'),
		collapseRowAria: t('datagridCollapseRowAria'),
		noExpandedContent: t('datagridNoExpandedContent'),

		// Pagination
		previousPage: t('datagridPreviousPage'),
		nextPage: t('datagridNextPage'),
		perPage: (size) => t('datagridPerPage', { size: String(size) }),
		availablePageSizes: t('datagridAvailablePageSizes'),
		showingRows: (start, end, total) =>
			t('datagridShowingRows', {
				start: String(start),
				end: String(end),
				total: String(total)
			}),
		pageLabel: t('datagridPageLabel'),
		pageOf: (pageCount) => t('datagridPageOf', { pageCount: String(pageCount) }),

		// Grid settings / fullscreen
		gridSettings: t('datagridGridSettings'),
		exitFullscreen: t('datagridExitFullscreen'),
		maximizeGrid: t('datagridMaximizeGrid'),
		columnVisibility: t('datagridColumnVisibility'),
		hideColumnGroups: t('datagridHideColumnGroups'),
		showColumnGroups: t('datagridShowColumnGroups'),

		// Column header interactions
		dragToReorderColumn: t('datagridDragToReorderColumn'),
		sortColumn: t('datagridSortColumn'),
		dragToReorderRow: t('datagridDragToReorderRow'),
		resizeColumn: t('datagridResizeColumn'),
		resizeColumnHint: t('datagridResizeColumnHint'),
		columnMenu: t('datagridColumnMenu'),

		// Context menu
		copyCell: t('datagridCopyCell'),
		copyRow: t('datagridCopyRow'),
		editCell: t('datagridEditCell'),
		pinRow: t('datagridPinRow'),
		unpinRow: t('datagridUnpinRow'),
		expandRow: t('datagridExpandRow'),
		collapseRow: t('datagridCollapseRow'),
		editRow: t('datagridEditRow'),
		deleteRow: t('datagridDeleteRow'),

		// CRUD toolbar
		addItem: t('datagridAddItem'),
		rowActions: t('datagridRowActions'),

		// Error state
		errorTitle: t('datagridErrorTitle'),
		errorRetry: t('datagridErrorRetry'),

		// Cell content expansion
		seeMore: t('datagridSeeMore'),
		seeLess: t('datagridSeeLess'),
		expandOverflowingCells: t('datagridExpandOverflowingCells'),
		collapseOverflowingCells: t('datagridCollapseOverflowingCells'),

		// Column header dropdown menu
		columnGroupHeading: (header) => t('datagridColumnGroupHeading', { header }),
		clearSort: t('datagridClearSort'),
		sortAscending: t('datagridSortAscending'),
		sortDescending: t('datagridSortDescending'),
		clearFilter: t('datagridClearFilter'),
		filterOperator: t('datagridFilterOperator'),
		groupByColumn: (header) => t('datagridGroupByColumn', { header }),
		ungroupByColumn: (header) => t('datagridUngroupByColumn', { header }),
		pinToLeft: t('datagridPinToLeft'),
		pinToRight: t('datagridPinToRight'),
		unpin: t('datagridUnpin'),
		hideColumn: t('datagridHideColumn'),
		fitToContent: t('datagridFitToContent'),
		resetColumnWidth: t('datagridResetColumnWidth'),
		moveLeft: t('datagridMoveLeft'),
		moveRight: t('datagridMoveRight'),
		moveTo: t('datagridMoveTo'),
		rootLevel: t('datagridRootLevel'),

		// Filter operators
		filterContains: t('datagridFilterContains'),
		filterNotContains: t('datagridFilterNotContains'),
		filterEquals: t('datagridFilterEquals'),
		filterNotEquals: t('datagridFilterNotEquals'),
		filterGreaterThan: t('datagridFilterGreaterThan'),
		filterLessThan: t('datagridFilterLessThan'),
		filterGreaterThanOrEqual: t('datagridFilterGreaterThanOrEqual'),
		filterLessThanOrEqual: t('datagridFilterLessThanOrEqual'),
		filterBetween: t('datagridFilterBetween'),
		filterEmpty: t('datagridFilterEmpty'),
		filterNotEmpty: t('datagridFilterNotEmpty'),
		filterStartsWith: t('datagridFilterStartsWith'),
		filterEndsWith: t('datagridFilterEndsWith'),

		// Filter input UI
		filterMode: (operator) => t('datagridFilterMode', { operator }),
		filterMin: (value) => t('datagridFilterMin', { value: filterBoundText(value) }),
		filterMax: (value) => t('datagridFilterMax', { value: filterBoundText(value) }),

		// Control center
		controlCenterHeading: t('datagridControlCenterHeading'),
		createColumnGroup: t('datagridCreateColumnGroup'),
		groupDataByColumn: t('datagridGroupDataByColumn'),
		sorting: t('datagridSorting'),
		holdShiftToSingleSort: t('datagridHoldShiftToSingleSort'),
		visibility: t('datagridVisibility'),
		exporting: t('datagridExporting'),
		freezing: t('datagridFreezing'),
		reordering: t('datagridReordering'),
		resizing: t('datagridResizing'),
		groupNamePlaceholder: t('datagridGroupNamePlaceholder'),
		createGroup: t('datagridCreateGroup'),

		// Export
		exportToExcel: t('datagridExportToExcel'),
		exportToCsv: t('datagridExportToCsv'),
		exportToXml: t('datagridExportToXml'),
		exportToJson: t('datagridExportToJson'),

		// Freezing
		freezingNone: t('datagridFreezingNone'),
		freezingLeft: t('datagridFreezingLeft'),
		freezingRight: t('datagridFreezingRight'),

		// Column grouping
		selectColumns: t('datagridSelectColumns'),
		columnsLabel: t('datagridColumnsLabel')
	};
}

function filterBoundText(value: string | number): string {
	return typeof value === 'number' ? formatWithoutExponent(value) : value;
}
